import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Plus, Trash2, Save, Calendar, Plane, Zap, Ban, Star } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import DateInput from '@/components/ui/date-input';
import { formatTime, formatTimeRange } from '@/lib/utils';

const WEEKDAYS = [
  { value: 0, label: 'Montag', short: 'Mo' },
  { value: 1, label: 'Dienstag', short: 'Di' },
  { value: 2, label: 'Mittwoch', short: 'Mi' },
  { value: 3, label: 'Donnerstag', short: 'Do' },
  { value: 4, label: 'Freitag', short: 'Fr' },
  { value: 5, label: 'Samstag', short: 'Sa' },
  { value: 6, label: 'Sonntag', short: 'So' },
];

export default function TeacherAvailabilityTab({ teacherId, selfMode = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [slots, setSlots] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Absence form
  const [absStart, setAbsStart] = useState('');
  const [absEnd, setAbsEnd] = useState('');
  const [absReason, setAbsReason] = useState('vacation');

  // Online-Terminvergabe (Phase F3)
  const [slotsEnabled, setSlotsEnabled] = useState(true);
  const [overrides, setOverrides] = useState([]);
  const [ovType, setOvType] = useState('block');
  const [ovDate, setOvDate] = useState('');
  const [ovStart, setOvStart] = useState('14:00');
  const [ovEnd, setOvEnd] = useState('15:00');

  // Endpoint-Konfiguration: Admin verwendet /admin/teachers/{id}/... ,
  // Lehrer-Self-Service verwendet /teacher/... (ohne teacher_id, Server löst selbst auf).
  const availBase = selfMode
    ? '/teacher/availability'
    : `/admin/teachers/${teacherId}/availability`;
  const absenceBase = selfMode
    ? '/teacher/absences'
    : `/admin/teachers/${teacherId}/absences`;
  const slotPath = selfMode
    ? '/teacher/slots'
    : `/admin/teachers/${teacherId}/slots`;
  const settingsPath = selfMode
    ? '/teacher/slot-settings'
    : `/admin/teachers/${teacherId}/slot-settings`;
  const overridesPath = selfMode
    ? '/teacher/slot-overrides'
    : `/admin/teachers/${teacherId}/slot-overrides`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, s, o] = await Promise.all([
        api.get(availBase),
        api.get(absenceBase),
        api.get(settingsPath),
        api.get(overridesPath),
      ]);
      setItems(a.data);
      setAbsences(b.data);
      setSlotsEnabled(!!s.data.slots_enabled);
      setOverrides(o.data);
    } catch (e) {
      toast.error('Fehler beim Laden der Verfügbarkeiten');
    } finally {
      setLoading(false);
    }
  }, [availBase, absenceBase, settingsPath, overridesPath]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const map = {};
    WEEKDAYS.forEach((w) => { map[w.value] = []; });
    items.forEach((it) => {
      if (map[it.weekday]) map[it.weekday].push(it);
    });
    Object.values(map).forEach((arr) => arr.sort((x, y) => x.start_time.localeCompare(y.start_time)));
    return map;
  }, [items]);

  const addBlock = (weekday) => {
    setItems((prev) => [
      ...prev,
      { weekday, start_time: '14:00', end_time: '17:00', _new: true, _id: `tmp-${Date.now()}-${Math.random()}` },
    ]);
  };

  const updateBlock = (idx, field, value) => {
    setItems((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const removeBlock = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const payload = items.map((it) => ({
        weekday: Number(it.weekday),
        start_time: it.start_time,
        end_time: it.end_time,
        location_id: it.location_id || null,
      }));
      const r = await api.put(availBase, { items: payload });
      setItems(r.data);
      toast.success('Verfügbarkeiten gespeichert');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Speichern fehlgeschlagen';
      toast.error(typeof detail === 'string' ? detail : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const addAbsence = async () => {
    if (!absStart || !absEnd) {
      toast.error('Start- und End-Datum sind erforderlich');
      return;
    }
    try {
      const r = await api.post(absenceBase, {
        start_date: absStart, end_date: absEnd, reason: absReason || null,
      });
      setAbsences((prev) => [r.data, ...prev]);
      setAbsStart(''); setAbsEnd('');
      toast.success('Abwesenheit angelegt');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Anlegen fehlgeschlagen';
      toast.error(typeof detail === 'string' ? detail : 'Anlegen fehlgeschlagen');
    }
  };

  const removeAbsence = async (id) => {
    try {
      await api.delete(`${absenceBase}/${id}`);
      setAbsences((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const toggleSlotsEnabled = async (v) => {
    setSlotsEnabled(v);
    try {
      await api.put(settingsPath, { slots_enabled: v });
      toast.success(v ? 'Kontingent für Online-Anmeldungen geöffnet' : 'Kontingent gesperrt');
    } catch (e) {
      setSlotsEnabled(!v);
      toast.error('Umschalten fehlgeschlagen');
    }
  };

  const addOverride = async () => {
    if (!ovDate) { toast.error('Datum ist erforderlich'); return; }
    try {
      const r = await api.post(overridesPath, {
        type: ovType, date: ovDate, start_time: ovStart, end_time: ovEnd,
      });
      setOverrides((prev) => [...prev, r.data].sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time)));
      setOvDate('');
      toast.success(ovType === 'block' ? 'Sperrtermin angelegt' : 'Wunschtermin angelegt');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Anlegen fehlgeschlagen');
    }
  };

  const removeOverride = async (id) => {
    try {
      await api.delete(`${overridesPath}/${id}`);
      setOverrides((prev) => prev.filter((o) => o.id !== id));
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const previewSlots = async () => {
    setPreviewLoading(true);
    try {
      const r = await api.get(slotPath, {
        params: { duration_minutes: 45, horizon_days: 21, limit: 5 },
      });
      setSlots(r.data);
    } catch (e) {
      toast.error('Vorschau fehlgeschlagen');
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 flex items-center gap-3 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Lade Verfügbarkeiten …
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="teacher-availability-tab">
      {/* Online-Terminvergabe (Phase F3) */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-amber-50 text-brand-red flex items-center justify-center shrink-0">
              <Zap className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Online-Terminvergabe</h2>
              <p className="text-sm text-slate-600 mt-0.5">
                Ist das Kontingent geöffnet, berechnet das System freie Termine automatisch aus dem
                Stundenplan: Lücken zwischen den Unterrichten sowie bis zu 60 Minuten vor der ersten
                und nach der letzten Stunde. Gepflegte Wochen-Zeitfenster (unten) haben Vorrang.
              </p>
            </div>
          </div>
          <label className="flex items-center gap-2 shrink-0 text-sm text-slate-700">
            <Switch checked={slotsEnabled} onCheckedChange={toggleSlotsEnabled} data-testid="slots-enabled-toggle" />
            {slotsEnabled ? 'Geöffnet' : 'Gesperrt'}
          </label>
        </div>

        {/* Sperr-/Wunschtermine */}
        <div className="mt-5 border-t border-slate-100 pt-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Sperr- &amp; Wunschtermine</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <div>
              <label className="text-xs text-slate-500">Typ</label>
              <Select value={ovType} onValueChange={setOvType}>
                <SelectTrigger data-testid="override-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="block">Sperrtermin</SelectItem>
                  <SelectItem value="offer">Wunschtermin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Datum</label>
              <DateInput  value={ovDate} onChange={(e) => setOvDate(e.target.value)} data-testid="override-date" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Von</label>
              <Input type="time" value={ovStart} onChange={(e) => setOvStart(e.target.value)} data-testid="override-start" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Bis</label>
              <Input type="time" value={ovEnd} onChange={(e) => setOvEnd(e.target.value)} data-testid="override-end" />
            </div>
            <Button onClick={addOverride} data-testid="override-add-btn">
              <Plus className="h-4 w-4 mr-1" /> Hinzufügen
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Sperrtermin = Zeitfenster wird nicht angeboten · Wunschtermin = Zeitfenster wird zusätzlich angeboten.
          </p>

          {overrides.length > 0 && (
            <div className="mt-3 space-y-2">
              {overrides.map((o) => (
                <div key={o.id} className="flex items-center justify-between border border-slate-200 rounded-lg p-2.5 bg-slate-50/50" data-testid={`override-row-${o.id}`}>
                  <div className="flex items-center gap-2 text-sm">
                    {o.type === 'block'
                      ? <Ban className="h-3.5 w-3.5 text-rose-500" />
                      : <Star className="h-3.5 w-3.5 text-brand-red" />}
                    <span className="font-medium text-slate-800">{o.date}</span>
                    <span className="text-slate-500">{formatTimeRange(o.start_time, o.end_time)}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${o.type === 'block' ? 'bg-rose-50 text-rose-600' : 'bg-amber-50 text-amber-700'}`}>
                      {o.type === 'block' ? 'Sperrtermin' : 'Wunschtermin'}
                    </span>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => removeOverride(o.id)} className="h-7 w-7" data-testid={`override-delete-${o.id}`}>
                    <Trash2 className="h-3 w-3 text-rose-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Wochenmatrix */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Wöchentliche Verfügbarkeit</h2>
            <p className="text-sm text-slate-600">
              Diese Zeitfenster werden vom System genutzt, um Eltern bei der Online-Anmeldung
              freie Termine vorzuschlagen. Belegte Stunden, Probestunden und Abwesenheiten werden automatisch ausgeblendet.
            </p>
          </div>
          <Button onClick={saveAll} disabled={saving} data-testid="availability-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Speichern
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {WEEKDAYS.map((w) => (
            <div key={w.value} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50" data-testid={`availability-day-${w.value}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-slate-800">{w.label}</div>
                <Button size="sm" variant="ghost" onClick={() => addBlock(w.value)} data-testid={`availability-add-${w.value}`}>
                  <Plus className="h-3 w-3 mr-1" /> Slot
                </Button>
              </div>
              {grouped[w.value].length === 0 && (
                <div className="text-xs text-slate-400 py-2">Keine Verfügbarkeit</div>
              )}
              {items.map((it, idx) => it.weekday === w.value && (
                <div key={it.id || it._id || idx} className="flex items-center gap-2 mb-2">
                  <Input
                    type="time"
                    value={formatTime(it.start_time) || ''}
                    onChange={(e) => updateBlock(idx, 'start_time', e.target.value)}
                    className="h-8 px-2 text-sm"
                    data-testid={`availability-start-${w.value}-${idx}`}
                  />
                  <span className="text-slate-400">–</span>
                  <Input
                    type="time"
                    value={formatTime(it.end_time) || ''}
                    onChange={(e) => updateBlock(idx, 'end_time', e.target.value)}
                    className="h-8 px-2 text-sm"
                    data-testid={`availability-end-${w.value}-${idx}`}
                  />
                  <Button size="icon" variant="ghost" onClick={() => removeBlock(idx)} className="h-7 w-7" data-testid={`availability-remove-${w.value}-${idx}`}>
                    <Trash2 className="h-3 w-3 text-rose-500" />
                  </Button>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Abwesenheiten */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-3">
          <Plane className="h-4 w-4 text-brand-red" />
          <h2 className="text-lg font-semibold text-slate-900">Abwesenheiten</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-xs text-slate-500">Von</label>
            <DateInput  value={absStart} onChange={(e) => setAbsStart(e.target.value)} data-testid="absence-start" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Bis</label>
            <DateInput  value={absEnd} onChange={(e) => setAbsEnd(e.target.value)} data-testid="absence-end" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Grund</label>
            <Select value={absReason} onValueChange={setAbsReason}>
              <SelectTrigger data-testid="absence-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vacation">Urlaub</SelectItem>
                <SelectItem value="sick">Krank</SelectItem>
                <SelectItem value="training">Fortbildung</SelectItem>
                <SelectItem value="other">Sonstiges</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={addAbsence} data-testid="absence-add-btn">
            <Plus className="h-4 w-4 mr-1" /> Hinzufügen
          </Button>
        </div>

        {absences.length === 0 ? (
          <div className="text-sm text-slate-400 mt-4">Keine Abwesenheiten erfasst.</div>
        ) : (
          <div className="mt-4 space-y-2">
            {absences.map((a) => (
              <div key={a.id} className="flex items-center justify-between border border-slate-200 rounded-lg p-3 bg-slate-50/50" data-testid={`absence-row-${a.id}`}>
                <div className="text-sm">
                  <span className="font-medium text-slate-800">{a.start_date} – {a.end_date}</span>
                  {a.reason && <span className="ml-2 text-slate-500">· {a.reason}</span>}
                </div>
                <Button size="icon" variant="ghost" onClick={() => removeAbsence(a.id)} className="h-7 w-7">
                  <Trash2 className="h-3 w-3 text-rose-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Slot-Vorschau */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-brand-red" />
            <h2 className="text-lg font-semibold text-slate-900">Slot-Vorschau (45 Min, 21 Tage)</h2>
          </div>
          <Button variant="outline" size="sm" onClick={previewSlots} disabled={previewLoading} data-testid="slot-preview-btn">
            {previewLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calendar className="h-4 w-4 mr-2" />}
            Vorschau aktualisieren
          </Button>
        </div>
        {!slots && (
          <div className="text-sm text-slate-400">Klicken Sie auf „Vorschau", um die nächsten freien Slots zu sehen.</div>
        )}
        {slots && slots.length === 0 && (
          <div className="text-sm text-slate-400">Keine freien Slots gefunden — Kontingent geöffnet? Ohne Wochen-Zeitfenster wird die Kapazität aus dem Stundenplan abgeleitet.</div>
        )}
        {slots && slots.length > 0 && (
          <ul className="space-y-1 text-sm" data-testid="slot-preview-list">
            {slots.map((s, i) => (
              <li key={i} className="flex items-center gap-3 px-3 py-2 border border-slate-100 rounded-lg bg-slate-50/40">
                <span className="font-mono text-slate-700">{s.date}</span>
                <span className="text-slate-500">{formatTimeRange(s.start_time, s.end_time)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
